/**
 * SIL-476 — merges the Overview tab's monthly S2+S3 emissions points
 * (`assetUsePhaseSnapshot.periodKey`) with this tab's new monthly MWh points
 * (`telemetry_daily_agg`, grouped by calendar month) into one series keyed
 * by `periodKey`, for the combined bars+line chart. The two source surfaces
 * have independent history depths (2 months of use-phase vs up to 7 months
 * of telemetry per the readiness doc) — a period present in one but not the
 * other renders `null` for the missing half rather than a fabricated zero,
 * so the chart's bars/line can each stop where their own data does.
 *
 * @pure
 */
export interface MonthlyEmissionsInput {
  periodKey: string;
  scope2Kgco2e: number;
  scope3Kgco2e: number;
}

export interface MonthlyMwhInput {
  periodKey: string;
  mwh: number;
}

export interface MergedMonthlyPoint {
  periodKey: string;
  scope2Kgco2e: number | null;
  scope3Kgco2e: number | null;
  mwh: number | null;
}

export function mergeMonthlyEmissionsWithMwh(
  emissions: readonly MonthlyEmissionsInput[],
  mwh: readonly MonthlyMwhInput[],
): MergedMonthlyPoint[] {
  const byPeriod = new Map<string, MergedMonthlyPoint>();

  for (const e of emissions) {
    byPeriod.set(e.periodKey, {
      periodKey: e.periodKey,
      scope2Kgco2e: e.scope2Kgco2e,
      scope3Kgco2e: e.scope3Kgco2e,
      mwh: null,
    });
  }

  for (const m of mwh) {
    const existing = byPeriod.get(m.periodKey);
    if (existing) {
      existing.mwh = m.mwh;
    } else {
      byPeriod.set(m.periodKey, {
        periodKey: m.periodKey,
        scope2Kgco2e: null,
        scope3Kgco2e: null,
        mwh: m.mwh,
      });
    }
  }

  return Array.from(byPeriod.values()).sort((a, b) => a.periodKey.localeCompare(b.periodKey));
}
